/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import _has from "lodash/has";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import { getProductData } from "../productService";
import _includes from "lodash/includes";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromNumber,
  fromUserInputtedFloat,
  getConvertedFormDataBase,
  pickAndConvert,
  prepareFormDataBase,
  sanitizeAddressData,
  sanitizeOrganizationData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toNumber,
  toUserInputtedFloat,
  fromInsurance,
  toInsurance,
  fromIban,
  toIban,
} from "../formServiceFunctions";
import ApiClient from "../apiClient";
import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { Customer } from "../../types/Entities/Customer";
import _pick from "lodash/pick";
import _isEmpty from "lodash/isEmpty";
import { Person } from "../../types/Entities/Person";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (product: AbstractCase): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_BASE]: {
      [product.name]: [
        "caseCategory",
        "affectedPartyType",
        "initialClientDescriptionAndObjectives",
        "clientWasMinorAtContractDate",
        "objectivesClient",
        "clientHasAlreadyClaimed",
        "claimedDateClient",
        "claimedDeadlineClient",
        "claimedAmountClient",
        "statementOfClaimFiledDate",
        "clientLegalRepresentativeAmount",
        "clientLegalRepresentativesAuthorizationType",
        "isDamageQuantifiable",
      ],
      client: ["gender", "givenName", "familyName", "birthDate"],
      clientBankData: ["iban"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      clientLegalRepresentative: ["gender", "givenName", "familyName"],
      "clientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      secondClientLegalRepresentative: ["gender", "givenName", "familyName"],
      "secondClientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
      "customer.person.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      additionalClients: [],
    },
    [applicationStatuses.APPLICATION_STATUS_INCIDENT]: {
      [product.name]: [
        "incidentDate",
        "incidentFrequency",
        "hasPreciseIncidentAddress",
        "incidentLocalityDescription",
        "policeInvolved",
        "policeReportReferenceNumber",
        "policeDepartmentName",
      ],
      incidentAddress: ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_REFINEMENT]: {
      [product.name]: ["refinementAnswers"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONCERN_SUMMARY]: {
      [product.name]: ["finalClientDescriptionAndObjectives"],
    },
    [applicationStatuses.APPLICATION_STATUS_PARTNER]: {
      [product.name]: ["opposingPartyType", "hasWitness", "witnessCount"],
      opposingPartyLegalEntity: ["legalName", "email", "telephone"],
      witnesses: ["gender", "givenName", "familyName"],
      witnessesToBeRemoved: ["id"],
      "opposingPartyLegalEntity.address": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      opposingPartyPerson: ["gender", "givenName", "familyName", "email", "telephone"],
      "opposingPartyPerson.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: {},
    [applicationStatuses.APPLICATION_STATUS_INSURANCE]: {
      insurance: ["insurance", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType", "deductible"],
      "insurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      [product.name]: ["powerOfAttorney"],
    },
  });

  conversionFields = [
    {
      path: "clientBankData.iban",
      convertFrom: fromIban,
      convertTo: toIban,
    },
    {
      path: "contractualPartnerHasLawyer",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "incidentDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "client.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "customer.person.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientHasAlreadyClaimed",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "isDamageQuantifiable",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "clientWasMinorAtContractDate",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "policeInvolved",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "hasWitness",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },

    {
      path: "claimedDateClient",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "claimedDeadlineClient",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "legalExpenseInsurance",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "claimedAmountClient",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "insurance.deductible",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "clientLegalRepresentativeAmount",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "insurance.insurance",
      convertFrom: fromInsurance,
      convertTo: toInsurance,
    },
  ];

  hasChangedData = (data: Record<string, any>, originalData: Record<string, any>, ignoreKeys: string[] = []) => {
    const ignoreSet = new Set(ignoreKeys);

    return Object.keys(data).some((key) => !ignoreSet.has(key) && data[key] !== originalData[key]);
  };

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    createMutation,
    refreshCustomer,
    deleteMutation,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields(product)[applicationStatus] || {};
    const convertedFormData = getConvertedFormDataBase(formData, this.conversionFields);

    let hasCustomerUpdates = false;
    let clearGenerationFields = false;

    if (_has(fields, "customer.person.residenceAddress") && !!customer.person?.residenceAddress?.id) {
      let addressData = pickAndConvert(formData.customer.person.residenceAddress, fields["customer.person.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);

      if (this.hasChangedData(addressData, customer.person.residenceAddress)) {
        hasCustomerUpdates = true;
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: customer.person.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      const convertedFormData = this.getConvertedFormData(formData);
      let customerPersonData = pickAndConvert(convertedFormData.customer.person, fields["customer.person"]);

      customerPersonData = sanitizePersonData(customerPersonData);

      if (this.hasChangedData(customerPersonData, customer.person)) {
        hasCustomerUpdates = true;
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/people",
          id: customer.person.id,
          data: customerPersonData,
        });
      }
    }

    if (_has(fields, "customer")) {
      const customerData = pickAndConvert(formData.customer, fields.customer);

      if (this.hasChangedData(customerData, product.customer)) {
        hasCustomerUpdates = true;
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/customers",
          id: customer.id,
          data: customerData,
        });
      }
    }

    if (_has(fields, "witnesses")) {
      formData.witnesses.map(async (witness: any) => {
        const witnessData = {
          givenName: witness.givenName,
          familyName: witness.familyName,
          gender: witness.gender,
          type: "processParticipant",
          labelType: "witness",
          customerSpecifiedCaseId: product.id,
        };

        if (typeof witness.id === "string" && witness.id.startsWith("new-")) {
          clearGenerationFields = true;

          await createMutation.mutateAsync({
            uri: "/people",
            data: witnessData,
          });
        } else {
          const originalData = product.witnesses.find((witnessItem: Person) => witnessItem.id === witness.id);

          if (this.hasChangedData(witnessData, originalData, ["customerSpecifiedCaseId"])) {
            clearGenerationFields = true;

            await updateMutation.mutateAsync({
              uri: "/people",
              id: witness.id,
              data: witnessData,
            });
          }
        }
      });
    }

    if (_has(fields, "witnessesToBeRemoved")) {
      if (formData.witnessesToBeRemoved.length > 0) {
        clearGenerationFields = true;
      }

      formData.witnessesToBeRemoved.map(async (id: number) => {
        await deleteMutation.mutateAsync({
          uri: "/people",
          id: id,
        });
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      const baseAddressData = formData.affectedPartyType === "customer" ? formData.customer.person.residenceAddress : formData.client.residenceAddress;
      let addressData = pickAndConvert(baseAddressData, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);

      if (this.hasChangedData(addressData, product.client.residenceAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.client.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "client")) {
      const convertedFormData = this.getConvertedFormData(formData);
      const baseClientData = convertedFormData.affectedPartyType === "customer" ? convertedFormData.customer.person : convertedFormData.client;
      let clientData = pickAndConvert(baseClientData, fields.client);
      clientData = sanitizePersonData(clientData);

      if (this.hasChangedData(clientData, product.client)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.client.id,
          data: clientData,
        });
      }
    }

    if (_has(fields, "clientBankData")) {
      const clientBankData = pickAndConvert(formData.clientBankData, fields["clientBankData"]);
      if (clientBankData.iban) {
        clientBankData.iban = toIban(clientBankData.iban);
      } else {
        delete clientBankData.iban;
      }
      if (!_isEmpty(clientBankData) && this.hasChangedData(clientBankData, product.clientBankData)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/bank_datas",
          id: formData.clientBankData.id,
          data: clientBankData,
        });
      }
    }

    if (_has(fields, "clientLegalRepresentative")) {
      let clientLegalRepresentativeData = pickAndConvert(formData.clientLegalRepresentative, fields.clientLegalRepresentative);
      clientLegalRepresentativeData = sanitizePersonData(clientLegalRepresentativeData);

      if (this.hasChangedData(clientLegalRepresentativeData, product.clientLegalRepresentative)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.clientLegalRepresentative.id,
          data: clientLegalRepresentativeData,
        });
      }
    }

    if (_has(fields, "clientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.clientLegalRepresentative.residenceAddress, fields["clientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);

      if (this.hasChangedData(addressData, product.clientLegalRepresentative.residenceAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.clientLegalRepresentative.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "secondClientLegalRepresentative")) {
      let secondClientLegalRepresentativeData = pickAndConvert(formData.secondClientLegalRepresentative, fields.secondClientLegalRepresentative);
      secondClientLegalRepresentativeData = sanitizePersonData(secondClientLegalRepresentativeData);

      if (this.hasChangedData(secondClientLegalRepresentativeData, product.secondClientLegalRepresentative)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.secondClientLegalRepresentative.id,
          data: secondClientLegalRepresentativeData,
        });
      }
    }

    if (_has(fields, "secondClientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.secondClientLegalRepresentative.residenceAddress, fields["secondClientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);

      if (this.hasChangedData(addressData, product.secondClientLegalRepresentative.residenceAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.secondClientLegalRepresentative.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (formData.opposingPartyType === "opposingPartyLegalEntity") {
      if (_has(fields, "opposingPartyLegalEntity")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyLegalEntity, fields.opposingPartyLegalEntity);
        opposingPartyData = sanitizeOrganizationData(opposingPartyData);

        if (this.hasChangedData(opposingPartyData, product.opposingPartyLegalEntity)) {
          clearGenerationFields = true;

          await updateMutation.mutateAsync({
            uri: "/organizations",
            id: product.opposingPartyLegalEntity.id,
            data: opposingPartyData,
          });
        }
      }

      if (_has(fields, "opposingPartyLegalEntity.address")) {
        let addressData = pickAndConvert(formData.opposingPartyLegalEntity.address, fields["opposingPartyLegalEntity.address"]);
        addressData = sanitizeAddressData(addressData);

        if (this.hasChangedData(addressData, product.opposingPartyLegalEntity.address)) {
          clearGenerationFields = true;

          await updateMutation.mutateAsync({
            uri: "/addresses",
            id: product.opposingPartyLegalEntity.address.id,
            data: addressData,
          });
        }
      }
    }

    if (formData.opposingPartyType === "opposingPartyPerson") {
      if (_has(fields, "opposingPartyPerson")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyPerson, fields.opposingPartyPerson);
        opposingPartyData = sanitizePersonData(opposingPartyData);

        if (this.hasChangedData(opposingPartyData, product.opposingPartyPerson)) {
          clearGenerationFields = true;
          await updateMutation.mutateAsync({
            uri: "/people",
            id: product.opposingPartyPerson.id,
            data: opposingPartyData,
          });
        }
      }
      if (_has(fields, "opposingPartyPerson.residenceAddress")) {
        let addressData = pickAndConvert(formData.opposingPartyPerson.residenceAddress, fields["opposingPartyPerson.residenceAddress"]);
        addressData = sanitizeAddressData(addressData);

        if (this.hasChangedData(addressData, product.opposingPartyPerson.residenceAddress)) {
          clearGenerationFields = true;

          await updateMutation.mutateAsync({
            uri: "/addresses",
            id: product.opposingPartyPerson.residenceAddress.id,
            data: addressData,
          });
        }
      }
    }

    if (_has(fields, "incidentAddress")) {
      let addressData = _pick(convertedFormData.incidentAddress, fields.incidentAddress);
      addressData = sanitizeAddressData(addressData);

      if (this.hasChangedData(addressData, product.incidentAddress)) {
        clearGenerationFields = true;

        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.incidentAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "insurance")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let insuranceData = pickAndConvert(convertedFormData.insurance, fields["insurance"]);
      await ApiClient.put("/customer_insurances/" + product.insurance.id, { body: JSON.stringify(insuranceData) });
    }

    if (_has(fields, "insurance.policyHolder")) {
      const basePolicyHolderData = formData.insurance.policyHolderType === "me" ? formData.customer.person : formData.insurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["insurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.insurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    let productData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      productData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, product.name)) {
      productData = _merge(productData, pickAndConvert(formData, fields[product.name]), pickAndConvert(formData, fields.product));
      productData = this.getConvertedFormData(productData);
    }
    if (hasCustomerUpdates) {
      await refreshCustomer();
    }

    if (nextApplicationStatus === applicationStatuses.APPLICATION_STATUS_REFINEMENT) {
      productData["generationRequest"] = "initialAssessment";
    } else if (nextApplicationStatus === applicationStatuses.APPLICATION_STATUS_CONCERN_SUMMARY) {
      productData["generationRequest"] = "finalAssessment";
    }

    productData["clearGenerationFields"] = clearGenerationFields;

    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: productData,
    });
  };

  getConvertedFormData = (data: Partial<AbstractCase>) => {
    return getConvertedFormDataBase(data, this.conversionFields);
  };

  getFrontendOnlyFieldsInitialValues = (product: AbstractCase) => ({
    howManyClients: product.additionalClients?.length ? "twoClients" : "",
  });

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    let formData = prepareFormDataBase(product, customer, this.conversionFields);
    return { ...this.getFrontendOnlyFieldsInitialValues(product), ...formData };
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_BASE]);

    if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory)) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INCIDENT]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PARTNER]);
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_REFINEMENT]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONCERN_SUMMARY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    //for dependencies that are changed in the same step as we are we need to check product instead of formdata to not
    // invalidate our next step since formdata updates before the actual request is fired
    if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory) && !product.powerOfAttorney) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);

    return availableSteps;
  };
}

export default new FormService();
